import React, { useState, useEffect, useRef } from 'react';
import { THEME_B } from '../curriculumConfig';
import { THEME_A } from '../themeAConfig';
import { PlayIcon, ChevronRightIcon } from './Icons';

function LockIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="8" width="10" height="7" rx="1.5" />
      <path d="M5 8V5.5a3 3 0 0 1 6 0V8" />
    </svg>
  );
}

function ChevronDownIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg">
      <polyline points="3 6 8 11 13 6" />
    </svg>
  );
}

function XIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      xmlns="http://www.w3.org/2000/svg">
      <line x1="3" y1="3" x2="13" y2="13" />
      <line x1="13" y1="3" x2="3" y2="13" />
    </svg>
  );
}

function SectionAccordion({ section, activeTopicId, onSelectTopic, isTopicLocked }) {
  // Start collapsed, matching the Theme A sections — the drawer opens tidy.
  const [open, setOpen] = useState(false);

  return (
    <div className="cnd-section">
      <button
        className="cnd-section-header"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="cnd-section-label">{section.title}</span>
        {section.hlOnly && <span className="cnd-hl-badge">HL</span>}
        <span className={`cnd-chevron ${open ? 'cnd-chevron-open' : ''}`}>
          <ChevronDownIcon />
        </span>
      </button>

      {open && (
        <div className="cnd-topics">
          {section.topics.map(topic => {
            const isActive = topic.id === activeTopicId;
            const locked   = !topic.isAvailable || isTopicLocked?.(topic);
            return (
              <button
                key={topic.id}
                className={[
                  'cnd-topic',
                  locked ? 'cnd-topic-locked' : 'cnd-topic-available',
                  isActive ? 'cnd-topic-active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => !locked && onSelectTopic(topic)}
                disabled={locked}
                title={locked && topic.isAvailable
                  ? 'Complete all Theme B exercises to unlock'
                  : undefined}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="cnd-topic-id ib-mono">{topic.id}</span>
                <span className="cnd-topic-title">{topic.title.replace(`${topic.id} `, '')}</span>
                <span className="cnd-topic-icon">
                  {locked
                    ? <LockIcon size={11} />
                    : <PlayIcon size={10} />}
                </span>
                {topic.hlOnly && !section.hlOnly && (
                  <span className="cnd-hl-badge">HL</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ThemeASectionAccordion({ topic, onSelectScope }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="cnd-section">
      <button
        className="cnd-section-header"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="cnd-section-label">
          <span aria-hidden="true" style={{ marginRight: 7 }}>{topic.icon}</span>
          {topic.title}
        </span>
        <span className={`cnd-chevron ${open ? 'cnd-chevron-open' : ''}`}>
          <ChevronDownIcon />
        </span>
      </button>

      {open && (
        <div className="cnd-topics">
          {topic.subtopics.map(sub => (
            <button
              key={sub.scope}
              className="cnd-topic cnd-topic-available"
              onClick={() => onSelectScope(sub.scope)}
            >
              <span className="cnd-topic-id ib-mono">{sub.scope}</span>
              <span className="cnd-topic-title">{sub.title.replace(`${sub.scope} `, '')}</span>
              <span className="cnd-topic-icon"><ChevronRightIcon size={10} /></span>
              {sub.hlOnly && <span className="cnd-hl-badge">HL</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CourseNavigationDrawer({
  open,
  onClose,
  activeTopicId,
  onSelectTopic,
  isTopicLocked,
  onNavigateToCodingLabs,
  onNavigateToTheoryReview,
  onNavigateToLocker,
  onNavigateToBits,
  onSelectThemeAScope,
  onNavigateToCPUS,
  onNavigateToPacks,
  onNavigateToHanoi,
  onNavigateToFractals,
  onNavigateToDiffusion,
  onNavigateToGan,
  onNavigateToConditional,
  onNavigateToLatent,
  onNavigateToFlow,
  onNavigateToCaverns,
  onNavigateToHelp,
  onNavigateToTeacher,
  isTeacher = false,
  onNavigateToAdmin,
  isAdmin = false,
  showThemeA = false,
}) {
  const drawerRef = useRef(null);

  // Collapsible "Simulators" group; auto-open when already on a simulator page.
  const SIM_PATHS = ['/cpus', '/packs', '/hanoi', '/fractals', '/diffusion', '/gan', '/conditional', '/latent', '/reversible', '/caverns'];
  const [simsOpen, setSimsOpen] = useState(() => SIM_PATHS.includes((window.location.pathname || '').replace(/\/+$/, '')));

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Trap focus: close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        onClose();
      }
    };
    // slight delay so the open-click doesn't immediately close
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`cnd-backdrop ${open ? 'cnd-backdrop-visible' : ''}`}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        ref={drawerRef}
        className={`cnd-drawer ${open ? 'cnd-drawer-open' : ''}`}
        aria-label="Course navigation"
        role="navigation"
      >
        <div className="cnd-header">
          <div>
            <div className="cnd-header-eyebrow">IB Computer Science</div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close navigation">
            <XIcon />
          </button>
        </div>

        {/* ── Global nav anchors (order mirrors the dashboard tab bar) ── */}
        <nav className="cnd-global-nav" aria-label="Global navigation">
          {(isTeacher || isAdmin) && (
            <>
              {isTeacher && (
                <button
                  className="cnd-global-nav-item"
                  onClick={() => { onNavigateToTeacher?.(); onClose(); }}
                >
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true">
                    <path d="M2 4.5 L8 2 L14 4.5 L8 7 Z" />
                    <path d="M4.5 6 V9.5 C4.5 10.5 11.5 10.5 11.5 9.5 V6" />
                  </svg>
                  My classes
                </button>
              )}
              {isAdmin && (
                <button
                  className="cnd-global-nav-item"
                  onClick={() => { onNavigateToAdmin?.(); onClose(); }}
                >
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true">
                    <circle cx="8" cy="5" r="2.4" />
                    <path d="M3.5 13c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" />
                  </svg>
                  Admin
                </button>
              )}
              <div className="cnd-global-nav-divider" aria-hidden="true" />
            </>
          )}
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToCodingLabs?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <polyline points="5 5 2 8 5 11" />
              <polyline points="11 5 14 8 11 11" />
            </svg>
            Coding Labs
          </button>
          {showThemeA && (
            <button
              className="cnd-global-nav-item"
              onClick={() => { onNavigateToTheoryReview?.(); onClose(); }}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="3" width="12" height="10" rx="1.5" />
                <line x1="2" y1="6" x2="14" y2="6" />
                <line x1="5" y1="9.5" x2="11" y2="9.5" />
              </svg>
              Theory Review
            </button>
          )}
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToLocker?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <rect x="2.5" y="7" width="11" height="7" rx="1.3" />
              <path d="M2.5 9.5h11" />
              <path d="M5.5 7V5.5a2.5 2.5 0 0 1 5 0V7" />
            </svg>
            The Locker
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToBits?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 4 2 8 6 12" />
              <polyline points="10 4 14 8 10 12" />
              <line x1="9" y1="2.5" x2="7" y2="13.5" />
            </svg>
            brookebits
          </button>
          <button
            className="cnd-global-nav-item"
            aria-expanded={simsOpen}
            onClick={() => setSimsOpen(o => !o)}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="12" height="9" rx="1.5" />
              <polygon points="7,6 10,7.6 7,9.2" fill="currentColor" stroke="none" />
              <line x1="5.5" y1="14" x2="10.5" y2="14" />
            </svg>
            Simulators
            <svg className={`cnd-sim-chevron${simsOpen ? ' cnd-sim-chevron-open' : ''}`}
              width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 3 11 8 6 13" />
            </svg>
          </button>
          {simsOpen && (
          <div className="cnd-sim-sublist">
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToCPUS?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="12" height="12" rx="2" />
              <line x1="6" y1="6" x2="10" y2="6" />
              <line x1="6" y1="9" x2="9" y2="9" />
            </svg>
            CPU Simulator
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToPacks?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <circle cx="3" cy="8" r="1.5" />
              <circle cx="8" cy="4" r="1.5" />
              <circle cx="8" cy="12" r="1.5" />
              <circle cx="13" cy="8" r="1.5" />
              <line x1="4.5" y1="7.2" x2="6.5" y2="4.8" />
              <line x1="4.5" y1="8.8" x2="6.5" y2="11.2" />
              <line x1="9.5" y1="4.8" x2="11.5" y2="7.2" />
              <line x1="9.5" y1="11.2" x2="11.5" y2="8.8" />
            </svg>
            Packet Switching
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToHanoi?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <line x1="1.5" y1="14" x2="14.5" y2="14" />
              <line x1="8" y1="2.5" x2="8" y2="14" />
              <rect x="4.5" y="10.5" width="7" height="2.2" rx="0.6" />
              <rect x="5.75" y="7.8" width="4.5" height="2.2" rx="0.6" />
              <rect x="6.5" y="5.1" width="3" height="2.2" rx="0.6" />
            </svg>
            Towers of Hanoi
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToFractals?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <polygon points="8,2 14,13 2,13" />
              <polygon points="8,7.5 11,13 5,13" />
            </svg>
            Fractal Drawing
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToDiffusion?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <rect x="1.8" y="1.8" width="5.4" height="5.4" rx="0.8" />
              <line x1="9.6" y1="4.5" x2="14.2" y2="4.5" />
              <circle cx="11.9" cy="11.5" r="2.6" />
            </svg>
            Diffusion Denoising
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToGan?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <circle cx="4" cy="8" r="2.4" />
              <circle cx="12" cy="8" r="2.4" />
              <path d="M6.4 6.6 L9.6 6.6 M6.4 9.4 L9.6 9.4" />
            </svg>
            GAN Loop
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToConditional?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="12" height="10" rx="1.5" />
              <path d="M5 10 L7 7 L9 9.5 L11 6.5" />
            </svg>
            Conditional Generation
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToLatent?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="12" height="12" rx="1.5" />
              <circle cx="6" cy="6" r="1" />
              <circle cx="11" cy="6" r="1" />
              <circle cx="8.5" cy="10.5" r="1.6" fill="currentColor" />
            </svg>
            VAE Latent Space
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToFlow?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <path d="M3 5.5 H11 M9 3.5 L11 5.5 L9 7.5" />
              <path d="M13 10.5 H5 M7 8.5 L5 10.5 L7 12.5" />
            </svg>
            Flow Reversibility
          </button>
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToCaverns?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <path d="M2 6 H6 A4 4 0 0 1 6 12 H2 Z" />
              <line x1="6" y1="9" x2="10" y2="9" />
              <circle cx="12" cy="9" r="1.4" />
            </svg>
            Boolean Caverns
          </button>
          </div>
          )}
          <button
            className="cnd-global-nav-item"
            onClick={() => { onNavigateToHelp?.(); onClose(); }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <circle cx="8" cy="8" r="6.5" />
              <path d="M6.1 6.2a1.9 1.9 0 0 1 3.7.6c0 1.3-1.9 1.7-1.9 2.9" />
              <line x1="8" y1="11.6" x2="8" y2="11.7" />
            </svg>
            Help &amp; Guide
          </button>
          <div className="cnd-global-nav-divider" aria-hidden="true" />
        </nav>

        <div className="cnd-body">
          {/* Theme A (theory) topics — above Theme B, matching dashboard order */}
          {showThemeA && (
            <>
              <div className="cnd-group-label">Theory · Theme A</div>
              {THEME_A.map(topic => (
                <ThemeASectionAccordion
                  key={topic.id}
                  topic={topic}
                  onSelectScope={(scope) => { onSelectThemeAScope?.(scope); onClose(); }}
                />
              ))}
              <div className="cnd-group-label">Coding · Theme B</div>
            </>
          )}
          {THEME_B.map(section => (
            <SectionAccordion
              key={section.id}
              section={section}
              activeTopicId={activeTopicId}
              onSelectTopic={onSelectTopic}
              isTopicLocked={isTopicLocked}
            />
          ))}
        </div>

        <div className="cnd-footer">
          <span className="cnd-footer-text">
            More units coming soon
          </span>
          <ChevronRightIcon size={11} />
        </div>
      </aside>
    </>
  );
}
